import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, Space, message, Card,
} from 'antd';
import { PlusOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const statusColors = {
  active: 'green',
  invited: 'blue',
  disabled: 'red',
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/users');
      setUsers(data.data || []);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await api.get('/api/users/roles');
      setRoles(data.data || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const normalizePhone = (phone) => {
    if (!phone) return phone;
    const digits = phone.replace(/[\s\-()]/g, '');
    if (/^04\d{8}$/.test(digits)) return '+61' + digits.slice(1);
    return digits;
  };

  const handleCreate = async (values) => {
    setCreateLoading(true);
    values.phone = normalizePhone(values.phone);
    values.twofaMethod = 'sms';
    try {
      const { data } = await api.post('/api/users/create', values);
      if (data.success) {
        message.success('User created! They can use Forgot Password to set their password.');
        setCreateModalOpen(false);
        form.resetFields();
        fetchUsers();
      } else {
        message.error(data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRoleChange = async (userId, roleId) => {
    try {
      await api.put(`/api/users/${userId}/role`, { roleId });
      message.success('Role updated');
      fetchUsers();
    } catch {
      message.error('Failed to update role');
    }
  };

  const handleDisable = async (userId) => {
    Modal.confirm({
      title: 'Disable User',
      content: 'Are you sure you want to disable this user?',
      onOk: async () => {
        try {
          await api.put(`/api/users/${userId}/disable`);
          message.success('User disabled');
          fetchUsers();
        } catch {
          message.error('Failed to disable user');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim() || '-',
      responsive: ['sm'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      key: 'role',
      render: (_, record) => (
        <Select
          value={record.role_id}
          onChange={(val) => handleRoleChange(record.id, val)}
          style={{ width: 160 }}
          size="small"
          options={roles.map((r) => ({ label: r.role_name, value: r.id }))}
        />
      ),
      responsive: ['md'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={statusColors[status]}>{status}</Tag>,
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'Never',
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status !== 'disabled' && (
            <Button size="small" danger onClick={() => handleDisable(record.id)}>
              Disable
            </Button>
          )}
        </Space>
      ),
      responsive: ['sm'],
    },
  ];

  return (
    <PageLayout>
      <div className="page-header">
        <h1>User Management</h1>
        <p>Manage dashboard users and their access permissions</p>
      </div>

      <Card
        title="Users"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            Create User
          </Button>
        }
      >
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize,
            showSizeChanger: true,
            showTotal: (t) => `${t} users`,
            onChange: (page, size) => setPageSize(size),
            onShowSizeChange: (current, size) => setPageSize(size),
          }}
          scroll={{ x: 600 }}
        />
      </Card>

      <Modal
        title="Create User"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} placeholder="First name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} placeholder="Last name" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input prefix={<MailOutlined />} placeholder="user@powow.com.au" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Phone is required for SMS 2FA' }]}
            extra="e.g. 0432445980 or +61432445980"
          >
            <Input placeholder="0412345678" />
          </Form.Item>
          <Form.Item name="roleId" label="Role" rules={[{ required: true }]}>
            <Select
              placeholder="Select a role"
              options={roles.map((r) => ({ label: `${r.role_name} (${r.role_type})`, value: r.id }))}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={createLoading} block>
              Create User
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
}
